
import React, { useState, useEffect } from 'react';
import { fetchCaregiverById, ApiError } from '../features/Jobs/services/jobsAPI';
import { User } from '../App';
import ProgressBar from '../components/ProgressBar';

interface LoginPageProps {
  onLogin: (user: User) => void;
}

// Admin mode allows any ID to log in as a Staff member for testing/demo purposes.
const ADMIN_MODE: number = 1;

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [showCareProLogin, setShowCareProLogin] = useState(false);
  const [careProId, setCareProId] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [loginSuccessData, setLoginSuccessData] = useState<User | null>(null);

  const handleCareProIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 5) {
      setCareProId(value);
      setError('');
    }
  };

  const handleCareProLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (careProId.trim() === '') {
      setError('Care Pro ID cannot be empty.');
      return;
    }
    
    setIsVerifying(true);
    setError('');

    try {
      const caregiver = await fetchCaregiverById(careProId);

      if (caregiver) {
        const userData: User = {
          id: String(caregiver.id),
          firstName: caregiver.first_name,
          lastName: caregiver.last_name,
          mobile: caregiver.mobile || '',
          city: caregiver.add_city || '',
          country: caregiver.add_country || '',
          type: caregiver.type || 'guest', // Extracting type from API
        };

        setLoginSuccessData(userData);
      } else if (ADMIN_MODE === 1) {
        setLoginSuccessData({
          id: careProId,
          firstName: 'Care',
          lastName: 'Manager',
          type: 'staff',
        });
      } else {
        setError('Invalid Care Pro ID. Please check the ID and try again.');
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 502 ? 'Invalid Care Pro ID, please try again.' : 'Server error. Please try again later.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      console.error(err);
    } finally {
      setIsVerifying(false);
    }
  };
  
  const handleGuestLogin = () => {
    onLogin({ id: 'Guest', firstName: 'Guest', lastName: '', type: 'guest' });
  };

  useEffect(() => {
    if (loginSuccessData) {
      const timer = setTimeout(() => {
        onLogin(loginSuccessData);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loginSuccessData, onLogin]);

  if (loginSuccessData) {
    return (
       <div className="flex items-center justify-center min-h-screen bg-brand-gray">
         <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-2xl shadow-lg text-center">
            <ProgressBar message={`Welcome, ${loginSuccessData.firstName}!`} />
         </div>
       </div>
    );
  }

  const whatsappSignUpUrl = `https://wa.me/60165116389?text=${encodeURIComponent("Hi Care Concierge Academy, I'm interested in signing up as a Care Pro.")}`;

  return (
    <div className="flex items-center justify-center min-h-screen bg-brand-gray p-4">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-2xl shadow-lg text-center">
        <div>
          <img className="mx-auto h-24 w-auto" src="https://mycareconcierge.com/wp-content/uploads/2024/10/care_concierge_logo_300x300.png" alt="Care Concierge" />
          <h2 className="mt-6 text-2xl font-extrabold text-gray-900">Care Guardian Job Portal</h2>
          <p className="mt-2 text-sm text-gray-600">Secure access for Care Professionals</p>
        </div>

        {!showCareProLogin ? (
          <div className="space-y-4 pt-4">
            <button onClick={() => setShowCareProLogin(true)} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-purple hover:bg-brand-purple/90">
              Login As Care Pro
            </button>
            <button onClick={handleGuestLogin} className="w-full flex justify-center py-3 px-4 border border-gray-400 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              Login As Guest
            </button>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleCareProLoginSubmit}>
            <div className="rounded-md shadow-sm">
              <input
                type="text"
                required
                autoFocus
                value={careProId}
                onChange={handleCareProIdChange}
                maxLength={5}
                className="appearance-none rounded-md block w-full px-3 py-3 border border-gray-400 bg-gray-50 text-gray-900 text-center sm:text-sm"
                placeholder="Enter your Care Pro ID"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex items-center justify-between pt-2">
                <button type="button" onClick={() => setShowCareProLogin(false)} className="text-sm font-medium text-brand-purple">
                    &larr; Back
                </button>
                <button type="submit" disabled={isVerifying} className="w-1/2 flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-brand-purple hover:bg-brand-purple/90 disabled:bg-brand-light-purple">
                    {isVerifying ? 'Verifying...' : 'Login'}
                </button>
            </div>
          </form>
        )}
        
        <div className="pt-6 border-t border-gray-200">
          <a href={whatsappSignUpUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2.5 px-4 py-2 text-sm font-medium text-green-800 bg-green-100 border border-green-200 rounded-lg">
            <span>👉 New here? Sign up via WhatsApp.</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
